/**
 * MergeDialog — modal for previewing and applying a branch merge.
 *
 * Shows the three-way merge preview, highlights conflicts, and allows
 * force-merge if needed. When the backend returns 409 (owner approval
 * required), switches to the MergeApprovalDialog inline.
 */

import { useCallback, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Dialog,
  Flex,
  RadioGroup,
  ScrollArea,
  Text,
} from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useApplyMerge, useApplyMergeResolved, useMergePreview } from "../../hooks/useVersioning";
import { ApprovalsRequiredError } from "../../api/versioning";
import { MergeApprovalDialog } from "./MergeApprovalDialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceBranch: string;
  targetBranch?: string;
  currentUserId?: string;
  isProjectOwner?: boolean;
}

const ACTION_COLORS: Record<string, "red" | "green" | "yellow" | "gray" | "blue"> = {
  conflict: "red",
  added: "green",
  modified: "yellow",
  deleted: "red",
  target_only: "blue",
  no_action: "gray",
};

export function MergeDialog({
  open,
  onOpenChange,
  sourceBranch,
  targetBranch = "main",
  currentUserId = "",
  isProjectOwner = false,
}: Props) {
  const { projectId, setBranch } = useProvenanceContext();
  const { data: preview, isLoading } = useMergePreview(
    projectId,
    sourceBranch,
    targetBranch,
    open,
  );
  const mergeMutation = useApplyMerge(projectId);
  const mergeResolvedMutation = useApplyMergeResolved(projectId);
  const [success, setSuccess] = useState(false);
  const [approvalMode, setApprovalMode] = useState(false);
  // Per-path conflict resolutions: path -> "source" | "target"
  const [resolutions, setResolutions] = useState<Record<string, "source" | "target">>({});

  const conflicts = preview?.filter((p) => p.conflict) ?? [];
  const changes = preview?.filter((p) => p.action !== "no_action") ?? [];
  const hasConflicts = conflicts.length > 0;
  const allConflictsResolved = conflicts.every((c) => resolutions[c.path] != null);

  const setResolution = useCallback((path: string, choice: "source" | "target") => {
    setResolutions((prev) => ({ ...prev, [path]: choice }));
  }, []);

  function handleMerge() {
    if (hasConflicts && Object.keys(resolutions).length > 0) {
      // Use per-path resolution merge
      mergeResolvedMutation.mutate(
        { source: sourceBranch, target: targetBranch, resolutions },
        {
          onSuccess: () => {
            setSuccess(true);
            setBranch(targetBranch);
          },
          onError: (err) => {
            if (err instanceof ApprovalsRequiredError) {
              setApprovalMode(true);
            }
          },
        },
      );
    } else {
      // No conflicts or no resolutions — standard merge
      mergeMutation.mutate(
        { source: sourceBranch, target: targetBranch, force: false },
        {
          onSuccess: () => {
            setSuccess(true);
            setBranch(targetBranch);
          },
          onError: (err) => {
            if (err instanceof ApprovalsRequiredError) {
              setApprovalMode(true);
            }
          },
        },
      );
    }
  }

  function handleRetryMerge() {
    setApprovalMode(false);
    handleMerge();
  }

  function handleClose() {
    setSuccess(false);
    setResolutions({});
    setApprovalMode(false);
    mergeMutation.reset();
    mergeResolvedMutation.reset();
    onOpenChange(false);
  }

  if (approvalMode) {
    return (
      <MergeApprovalDialog
        open={open}
        onOpenChange={handleClose}
        projectId={projectId}
        sourceBranch={sourceBranch}
        targetBranch={targetBranch}
        currentUserId={currentUserId}
        isProjectOwner={isProjectOwner}
        onRetryMerge={handleRetryMerge}
      />
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content maxWidth="600px">
        <Dialog.Title>
          Merge <Text color="orange">{sourceBranch}</Text> into{" "}
          <Text color="blue">{targetBranch}</Text>
        </Dialog.Title>

        {success ? (
          <Flex direction="column" gap="3" mt="3">
            <Callout.Root color="green">
              <Callout.Text>
                Merge successful — {(mergeMutation.data?.merged_count ?? mergeResolvedMutation.data?.merged_count ?? 0)} change(s) applied.
              </Callout.Text>
            </Callout.Root>
            <Flex justify="end">
              <Button onClick={handleClose}>Done</Button>
            </Flex>
          </Flex>
        ) : (
          <>
            {isLoading && (
              <Text size="2" color="gray" mt="3">Computing merge preview...</Text>
            )}

            {/* Show 403 error for non-owners */}
            {mergeMutation.isError && !(mergeMutation.error instanceof ApprovalsRequiredError) && (
              <Callout.Root color="red" mt="3">
                <Callout.Text>
                  {mergeMutation.error.message.includes("403")
                    ? "Only project owners can merge branches."
                    : mergeMutation.error.message}
                </Callout.Text>
              </Callout.Root>
            )}

            {preview && (
              <Flex direction="column" gap="3" mt="3">
                <Flex gap="2">
                  <Badge color="green" size="1">
                    {changes.filter((c) => c.action === "added").length} added
                  </Badge>
                  <Badge color="yellow" size="1">
                    {changes.filter((c) => c.action === "modified").length} modified
                  </Badge>
                  <Badge color="red" size="1">
                    {changes.filter((c) => c.action === "deleted").length} deleted
                  </Badge>
                  {hasConflicts && (
                    <Badge color="red" size="1">
                      {conflicts.length} conflict{conflicts.length !== 1 ? "s" : ""}
                    </Badge>
                  )}
                </Flex>

                {hasConflicts && (
                  <Callout.Root color="red">
                    <Callout.Text>
                      {conflicts.length} conflict{conflicts.length !== 1 ? "s" : ""} detected.
                      Non-conflicting changes will still be merged. Resolve each conflict below
                      by choosing source or target.
                    </Callout.Text>
                  </Callout.Root>
                )}

                <ScrollArea style={{ maxHeight: 350 }}>
                  <Box asChild style={{ width: "100%", borderCollapse: "collapse" }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                            <Text size="1" weight="medium">Path</Text>
                          </th>
                          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                            <Text size="1" weight="medium">Action</Text>
                          </th>
                          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                            <Text size="1" weight="medium">Source</Text>
                          </th>
                          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                            <Text size="1" weight="medium">Target</Text>
                          </th>
                          {hasConflicts && (
                            <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--gray-a4)" }}>
                              <Text size="1" weight="medium">Resolve</Text>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {changes.map((entry) => (
                          <tr
                            key={entry.path}
                            style={{
                              background: entry.conflict
                                ? resolutions[entry.path]
                                  ? "var(--green-a2)"
                                  : "var(--red-a3)"
                                : undefined,
                            }}
                          >
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                              <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                                {entry.path}
                              </Text>
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                              <Badge size="1" color={
                                entry.conflict && resolutions[entry.path]
                                  ? "green"
                                  : ACTION_COLORS[entry.action] ?? "gray"
                              }>
                                {entry.conflict && resolutions[entry.path]
                                  ? `use ${resolutions[entry.path]}`
                                  : entry.action}
                              </Badge>
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                              <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                                {entry.source_value != null ? JSON.stringify(entry.source_value) : "—"}
                              </Text>
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                              <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                                {entry.target_value != null ? JSON.stringify(entry.target_value) : "—"}
                              </Text>
                            </td>
                            {hasConflicts && (
                              <td style={{ padding: "6px 8px", borderBottom: "1px solid var(--gray-a3)" }}>
                                {entry.conflict && (
                                  <RadioGroup.Root
                                    value={resolutions[entry.path] ?? ""}
                                    onValueChange={(v) => setResolution(entry.path, v as "source" | "target")}
                                    size="1"
                                  >
                                    <Flex gap="3">
                                      <Flex align="center" gap="1">
                                        <RadioGroup.Item value="source" />
                                        <Text size="1">Source</Text>
                                      </Flex>
                                      <Flex align="center" gap="1">
                                        <RadioGroup.Item value="target" />
                                        <Text size="1">Target</Text>
                                      </Flex>
                                    </Flex>
                                  </RadioGroup.Root>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </ScrollArea>

                {hasConflicts && !allConflictsResolved && (
                  <Callout.Root color="yellow" size="1">
                    <Callout.Text>
                      Resolve all {conflicts.length - Object.keys(resolutions).filter(k => conflicts.some(c => c.path === k)).length} remaining
                      conflict{conflicts.length - Object.keys(resolutions).filter(k => conflicts.some(c => c.path === k)).length !== 1 ? "s" : ""} to
                      enable merge.
                    </Callout.Text>
                  </Callout.Root>
                )}
              </Flex>
            )}

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">Cancel</Button>
              </Dialog.Close>
              <Button
                onClick={handleMerge}
                disabled={
                  mergeMutation.isPending ||
                  mergeResolvedMutation.isPending ||
                  (hasConflicts && !allConflictsResolved)
                }
                color={hasConflicts ? (allConflictsResolved ? "green" : "red") : undefined}
              >
                {mergeMutation.isPending || mergeResolvedMutation.isPending
                  ? "Merging..."
                  : hasConflicts && allConflictsResolved
                    ? "Merge (resolved)"
                    : "Merge"}
              </Button>
            </Flex>
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
